import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import useObTrainingsApi from './config';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import { useAuth } from '../../context/AuthContext';
import Button from '../../components/ui/Button';

const Container = styled.div``;

const Modal = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.5);
  display: ${(p) => (p.$open ? 'flex' : 'none')};
  align-items: center; justify-content: center; z-index: 1300; padding: 24px;
`;
const ModalCard = styled.div`
  background: #fff; border-radius: 8px; max-width: 480px; width: 100%; padding: 24px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.15);
`;
const Field = styled.div` display: flex; flex-direction: column; gap: 6px; margin-bottom: 14px; font-size: 14px; `;
const FieldLabel = styled.label` color: #64748b; font-weight: 500; `;
const FieldText = styled.textarea` padding: 8px 12px; border: 1px solid #cbd5e0; border-radius: 4px; font-size: 14px; resize: vertical; min-height: 60px; font-family: inherit; `;
const Actions = styled.div` display: flex; gap: 8px; justify-content: flex-end; margin-top: 16px; `;
// #72 — yerel Primary/Secondary (kopyalanmis blok — onboarding-*/ats-*
// sayfalarinda ayni tarif) kaldirildi, paylasilan Button'a baglandi
// (bkz. t6-report.md).

export default function ObTrainingsPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const writable = canWrite ? canWrite('OB') : false;

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);

  const [completeDialog, setCompleteDialog] = useState({ isOpen: false, id: null, person: '', training: '' });
  const [retakeDialog, setRetakeDialog] = useState({ isOpen: false, id: null, person: '', training: '', reason: '' });
  const [unassignDialog, setUnassignDialog] = useState({ isOpen: false, id: null, training: '' });
  const [busy, setBusy] = useState(false);

  // drawer: { mode: 'create' } | null
  const [drawer, setDrawer] = useState(null);

  const { fetching, data, pagination, columns: baseColumns, objectSchema, create, markCompleted, markRetake, unassign } = useObTrainingsApi(queryParams);

  const handleDataStateChange = useCallback((act, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => setFilterList(newFilterList), []);

  const handleNewButtonClick = useCallback(() => setDrawer({ mode: 'create' }), []);

  const handleSave = useCallback(async (formData) => {
    const success = await create(formData);
    if (success) setDrawer(null);
    return success;
  }, [create]);

  const buildActions = useCallback((row) => {
    if (!row || !writable) return [];
    const actions = [];
    if (!row.completedAt) {
      actions.push({ type: 'edit', label: t('onboardingTraining.action.complete'),
        onClick: () => setCompleteDialog({ isOpen: true, id: row.id, person: row.personFullName, training: row.trainingTitle }) });
    }
    if (row.completedAt && !row.retakeRequired) {
      actions.push({ type: 'details', label: t('onboardingTraining.action.retake'),
        onClick: () => setRetakeDialog({ isOpen: true, id: row.id, person: row.personFullName, training: row.trainingTitle, reason: '' }) });
    }
    actions.push({ type: 'divider' });
    actions.push({ type: 'delete', label: t('onboardingTraining.action.unassign'),
      onClick: () => setUnassignDialog({ isOpen: true, id: row.id, training: row.trainingTitle }) });
    return actions;
  }, [t, writable]);

  const columns = useMemo(() => {
    if (!writable) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions', label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            const actions = buildActions(row);
            return actions.length > 0 ? <OptionsMenu actions={actions} row={row} /> : null;
          },
        },
      },
    ];
  }, [baseColumns, data, buildActions, writable]);

  return (
    <Container>
      <GenericFormDrawer
        open={!!drawer}
        onClose={() => setDrawer(null)}
        title={t('onboardingTraining.new')}
        schema={objectSchema}
        initialValues={null}
        onSave={handleSave}
      />

      <ConfirmDialog
        isOpen={completeDialog.isOpen}
        title={t('onboardingTraining.action.complete')}
        message={t('onboardingTraining.completeConfirm', { person: completeDialog.person, training: completeDialog.training })}
        onConfirm={async () => { setBusy(true); await markCompleted(completeDialog.id); setCompleteDialog({ isOpen: false, id: null, person: '', training: '' }); setBusy(false); }}
        onCancel={() => setCompleteDialog({ isOpen: false, id: null, person: '', training: '' })}
        loading={busy}
      />

      <Modal $open={retakeDialog.isOpen}>
        {retakeDialog.isOpen && (
          <ModalCard>
            <h3 style={{ margin: '0 0 16px' }}>{t('onboardingTraining.action.retake')} — {retakeDialog.training}</h3>
            <Field>
              <FieldLabel>{t('onboardingTraining.retakeReason')}</FieldLabel>
              <FieldText value={retakeDialog.reason}
                onChange={(e) => setRetakeDialog({ ...retakeDialog, reason: e.target.value })} />
            </Field>
            <Actions>
              <Button variant="outline" onClick={() => setRetakeDialog({ isOpen: false, id: null, person: '', training: '', reason: '' })}>{t('common.cancel')}</Button>
              <Button disabled={busy} onClick={async () => {
                setBusy(true); await markRetake(retakeDialog.id, retakeDialog.reason || undefined);
                setRetakeDialog({ isOpen: false, id: null, person: '', training: '', reason: '' });
                setBusy(false);
              }}>{busy ? '...' : t('common.confirm')}</Button>
            </Actions>
          </ModalCard>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={unassignDialog.isOpen}
        title={t('onboardingTraining.action.unassign')}
        message={t('onboardingTraining.unassignConfirm', { training: unassignDialog.training })}
        onConfirm={async () => { setBusy(true); await unassign(unassignDialog.id); setUnassignDialog({ isOpen: false, id: null, training: '' }); setBusy(false); }}
        onCancel={() => setUnassignDialog({ isOpen: false, id: null, training: '' })}
        loading={busy}
      />

      {useMemo(() => (
        <DataTable
          title={t('onboardingTraining.pageTitle')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord={writable}
          onNewButtonClick={handleNewButtonClick}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      ), [
        t, fetching, columns, data, pagination, writable, handleNewButtonClick,
        filterList, handleFilterConfirm, handleDataStateChange,
      ])}
    </Container>
  );
}
